import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import client from 'utils/client';
import AppDataGrid from 'components/AppDataGrid/AppDataGrid';
import StatusPill, { ActivePill, TransactionStatusPill, SettledPill } from 'components/StatusPill/StatusPill';
import { formatCurrency, formatThousand } from 'utils/helpers';
import { useToast } from 'components/Toast/useToast';
import 'pages/merchants/detail/merchant-detail.css';

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Today' },
  { value: 'weekly', label: 'This week' },
  { value: 'monthly', label: 'This month' },
  { value: '3months', label: 'Last 3 months' },
  { value: '6months', label: 'Last 6 months' },
  { value: '1year', label: 'This year' },
];

const PERIOD_LABEL = {
  daily: 'today',
  weekly: 'this week',
  monthly: 'this month',
  '3months': 'last 3 months',
  '6months': 'last 6 months',
  '1year': 'this year',
};

const BRAND = '#1e3a8a';
const WARN = '#b45309';

function StatCard({ label, value, sub, tone }) {
  return (
    <div className={`md-stat ${tone ? `md-stat--${tone}` : ''}`}>
      <div className='md-stat-label'>{label}</div>
      <div className='md-stat-value tabular'>{value}</div>
      {sub ? <div className='md-stat-sub'>{sub}</div> : null}
    </div>
  );
}

function TerminalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { onOpen } = useToast();
  const [terminal, setTerminal] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    client.get(`/terminals/${id}`)
      .then((res) => setTerminal(res.data.data))
      .catch((err) => onOpen({ type: 'error', message: err?.message || 'Failed to load terminal' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setLoading(true);
    client.get(`/terminals/${id}/metrics?period=${period}`)
      .then((res) => setMetrics(res.data.data))
      .catch((err) => onOpen({ type: 'error', message: err?.message || 'Failed to load metrics' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, period]);

  const totals = metrics?.totals;
  const daily = metrics?.daily || [];
  const recent = metrics?.recentTransactions || [];
  const periodLabel = PERIOD_LABEL[period] || '';

  const recentColumns = [
    {
      field: 'reference',
      headerName: 'Reference',
      width: 260,
      renderCell: (p) => <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{p.row.reference}</span>,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => <TransactionStatusPill code={p.row.status} />,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (p) => formatCurrency(p.row.amount),
    },
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 180,
      valueGetter: (p) => dayjs(p.row.transaction_date || p.row.created_at).format('MMM DD, YYYY h:mma'),
    },
    {
      field: 'settled',
      headerName: 'Settled',
      width: 100,
      renderCell: (p) => <SettledPill settled={!!p.row.settled} />,
    },
  ];

  return (
    <div className='md-page'>
      <div className='md-breadcrumbs'>
        <Link to='/terminals' className='md-link'>Terminals</Link>
        <span>/</span>
        <span>{terminal?.terminal_id || `#${id}`}</span>
      </div>

      <div className='md-header'>
        <div className='md-header-left'>
          <span className='md-avatar' aria-hidden='true'>T</span>
          <div>
            <h1 className='md-name' style={{ fontFamily: 'ui-monospace, monospace' }}>
              {terminal?.terminal_id || '—'}
            </h1>
            <div className='md-subline'>
              <span>Serial: {terminal?.serial_number || '—'}</span>
              {terminal?.location ? <span>· {terminal.location}</span> : null}
              {terminal?.agent_name ? <span>· {terminal.agent_name}</span> : null}
              {terminal ? <ActivePill active={terminal.active} /> : null}
              {terminal ? (
                <StatusPill
                  label={terminal.instant_settlement_enabled ? 'Instant' : 'EOD'}
                  variant={terminal.instant_settlement_enabled ? 'info' : 'neutral'}
                />
              ) : null}
              {terminal?.settlement_process ? (
                <StatusPill
                  label={terminal.settlement_process.charAt(0) + terminal.settlement_process.slice(1).toLowerCase()}
                  variant={terminal.settlement_process === 'BULK' ? 'info' : 'neutral'}
                />
              ) : null}
            </div>
          </div>
        </div>
        <div className='md-header-right'>
          <button
            type='button'
            className='btn-secondary'
            onClick={() => navigate(`/edit-terminal/${id}`, { state: { terminal } })}
            disabled={!terminal}>
            Edit
          </button>
        </div>
      </div>

      <div className='md-toolbar'>
        <div className='md-period-pill'>
          {PERIOD_OPTIONS.map((o) => (
            <button
              key={o.value}
              type='button'
              className={`md-period-option ${period === o.value ? 'is-active' : ''}`}
              onClick={() => setPeriod(o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className='md-stats-row'>
        <StatCard
          label={`Settled ${periodLabel}`}
          value={formatCurrency(totals?.settledAmount || 0)}
          sub={`${formatThousand(totals?.settledCount || 0)} transactions`}
          tone='success'
        />
        <StatCard
          label='Pending settlement'
          value={formatCurrency(totals?.pendingAmount || 0)}
          sub={`${formatThousand(totals?.pendingCount || 0)} transactions`}
          tone='warning'
        />
        <StatCard
          label={`Total volume ${periodLabel}`}
          value={formatCurrency(totals?.txnAmount || 0)}
          sub={`${formatThousand(totals?.txnCount || 0)} transactions`}
        />
      </div>

      <div className='md-card'>
        <div className='md-card-head'>
          <div className='md-card-title'>Settled per day</div>
          <div className='md-card-hint'>Daily settled vs pending volume for this terminal.</div>
        </div>
        <div style={{ height: 260 }}>
          {loading ? (
            <div className='md-loading'>Loading…</div>
          ) : daily.length === 0 ? (
            <div className='md-empty'>No transactions in this period.</div>
          ) : (
            <ResponsiveContainer>
              <LineChart data={daily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
                <XAxis dataKey='bucket' tickFormatter={(v) => dayjs(v).format('MMM D')} tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tickFormatter={(v) => formatThousand(v)} tick={{ fontSize: 11, fill: '#64748b' }} width={70} />
                <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={(v) => dayjs(v).format('MMM D, YYYY')} />
                <Line type='monotone' dataKey='settledAmount' name='Settled' stroke={BRAND} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type='monotone' dataKey='pendingAmount' name='Pending' stroke={WARN} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className='md-card'>
        <div className='md-card-head'>
          <div className='md-card-title'>Recent transactions</div>
          <div className='md-card-hint'>Last 10 transactions on this terminal.</div>
        </div>
        <AppDataGrid
          className='md-grid'
          rows={recent}
          columns={recentColumns}
          loading={loading}
          height={Math.min(60 + (recent.length || 1) * 52, 580)}
          hideFooter
          disableColumnFilter
        />
      </div>
    </div>
  );
}

export default TerminalDetail;
